import { SerializedConstructor } from '@langchain/core/load/serializable';
import { FormControl, FormLabel } from '@mui/joy';

import {
  TEXT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import { cn } from '@/utils/tailwind';

import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
  tableVariantLabelClass,
  tableVariantRowClass,
} from '../../Manifest.utils';
import { ManifestDropdown } from '../../ManifestDropdown';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplaceWithSwitch } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { BedrockCommonRegionDropdown } from '../common/ManifestCommonBedrock';
import { ManifestChatAnthropicToolListEditor } from '../common/ManifestCommonChatAnthropic';
import { DEFAULT_MODEL, MODELS } from './ManifestBedrockChat.utils';

const BEDROCK_API_OPTIONS = ['Converse', 'InvokeModel (Legacy)'];
const BEDROCK_CLASS_TO_BEDROCK_API = {
  ChatBedrockConverse: 'Converse',
  ChatBedrock: 'InvokeModel (Legacy)',
};
const BEDROCK_API_TO_ID = {
  'InvokeModel (Legacy)': [
    'langchain',
    'chat_models',
    'bedrock',
    'ChatBedrock',
  ],
  Converse: ['langchain_aws', 'chat_models', 'ChatBedrockConverse'],
};

export function ManifestBedrockChat(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs ?? {};
  const model: string | undefined = kwargs.model_id || DEFAULT_MODEL;
  const id: string = props.value.id.at(-1) ?? 'ChatBedrockConverse';

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplaceWithSwitch
          variant={props.variant}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
          isChatModel={true}
          otherModelId={TEXT_MODEL_MAPPING['Bedrock'].modelId}
        />

        <ManifestSelectModel
          models={MODELS}
          onChange={(model_id) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model_id },
            });
          }}
          value={kwargs.model_id || DEFAULT_MODEL}
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <FormControl>
          <FormLabel>API</FormLabel>
          <ManifestDropdown<string>
            value={BEDROCK_CLASS_TO_BEDROCK_API[id]}
            onChange={(api) =>
              props.onChange({
                ...props.value,
                id: api == null ? props.value.id : BEDROCK_API_TO_ID[api],
                kwargs: {
                  ...kwargs,
                  ...(api === 'Converse' && {
                    beta_use_converse_api: undefined,
                    model_kwargs: undefined,
                    provider_stop_sequence_key_name_map: undefined,
                    provider_stop_reason_key_map: undefined,
                    streaming: undefined,
                    system_prompt_with_tools: undefined,
                    guardrails: undefined,
                  }),
                  ...(api === 'InvokeModel (Legacy)' && {
                    supports_tool_choice_values: undefined,
                    top_p: undefined,
                    additional_model_request_fields: undefined,
                    additional_model_response_field_paths: undefined,
                  }),
                },
              })
            }
            options={BEDROCK_API_OPTIONS}
          />
        </FormControl>

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={2}
          step={0.1}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Max Output Tokens"
          value={kwargs.max_tokens == null ? -1 : kwargs.max_tokens}
          min={-1}
          max={8000}
          step={1}
          onChange={(max_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: {
                ...kwargs,
                max_tokens: max_tokens === -1 ? null : max_tokens,
              },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        {props.variant === 'table' && props.readOnly ? (
          <div className={tableVariantRowClass}>
            <div className={tableVariantLabelClass}>Region</div>
            <div>{props.value.kwargs['region_name'] ?? ''}</div>
          </div>
        ) : (
          <FormControl>
            <FormLabel>Region</FormLabel>
            <BedrockCommonRegionDropdown
              regionName={props.value.kwargs['region_name'] ?? null}
              onChange={(region_name: string | null) =>
                props.onChange({
                  ...props.value,
                  kwargs: { ...kwargs, region_name },
                })
              }
            />
          </FormControl>
        )}

        {!props.hideTools && model?.split('.')?.at(0) === 'anthropic' && (
          <ManifestChatAnthropicToolListEditor {...props} />
        )}
      </div>
    </>
  );
}

export function ManifestBedrockChatPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs ?? {};
  const model = kwargs.model_id || DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
      </div>
    );
  }

  return null;
}
